import { track } from '@amplitude/analytics-browser';

interface TrackableEventParams {
    pageName: string;
    clientCode: string;
    action: string;
    additionalMetadata?: Record<string, any>;
    overrideMessage?: string | null;
    error?: any; // Added error property to match usage in trackEventFromErrorObject
}

/**
 * Represents a trackable event.
 *
 * Create one of these objects to comply with naming conventions for tracking events and error events.
 * These objects can be passed to track events or error events so if the event is created before a try
 * catch you pass the object to either the success or the error event without redefining anything.
 *
 * For errors you may also append TrackableEvent.error to the object and the tracked event will include the error message if available.
 *
 * @class
 */
class TrackableEvent {
    pageName: string;
    clientCode: string;
    action: string;
    additionalMetadata: Record<string, any>;
    overrideMessage: string | null;
    error: any | null = null;

    /**
     * Creates a new TrackableEvent instance.
     * @constructor
     * @param {TrackableEventParams} params - The parameters for the event
     */
    constructor({ pageName, clientCode, action, additionalMetadata = {}, overrideMessage = null }: TrackableEventParams) {
        this.pageName = pageName;
        this.clientCode = clientCode;
        this.action = action;
        this.additionalMetadata = additionalMetadata;
        this.overrideMessage = overrideMessage;
    }
}

const checkEnv = (): boolean => {
    // TODO: put this back in after testing in staging
    if (process.env.REACT_APP_APP_ENV !== 'production') {
         return false;
    }

    return true;
}

const trackBasicEvent = (eventName: string): void => {
    if (!checkEnv()) {
        return;
    }
    
    track(eventName);
}

const trackEvent = (eventName: string, eventData: Record<string, any>): void => {
    if (!checkEnv()) {
        return;
    }

    track(eventName, { ...eventData });
}

/**
 * Track an event from the trackableEvent object
 *
 * @param {TrackableEvent} trackableEvent
 * @returns
 */
const trackEventFromObject = (trackableEvent: TrackableEvent): void => {
    if (!checkEnv()) {
        return;
    }

    trackEvent(trackableEvent.pageName + ' Event', {
        action: trackableEvent.action,
        clientCode: trackableEvent.clientCode,
        ...trackableEvent.additionalMetadata,
    });
}

interface HandleTrackErrorParams {
    error: any;
    pageName?: string | null;
    overrideMessage?: string | null;
    additionalMetadata?: Record<string, any>;
}

const handleTrackError = ({ error, pageName = null, overrideMessage = null, additionalMetadata = {} }: HandleTrackErrorParams): void => {
    if (!checkEnv()) {
        return;
    }

    const message = (error.response?.data?.message || overrideMessage) || 'Unknown error';

    let metadata: Record<string, any> = {pageName, errorMessage: message};
    if (additionalMetadata) {
        metadata = {...metadata, ...additionalMetadata};
    }

    trackEvent(`${pageName ?? 'General'} Error`, metadata);
}

/**
 * Takes in a TrackableEvent object and sends an event to Amplitude
 *
 * @param {TrackableEvent} trackableEvent
 */
const trackEventFromErrorObject = (trackableEvent: TrackableEvent): void => {
    if (!checkEnv()) {
        return;
    }

    const message = (trackableEvent.error?.response?.data?.message || trackableEvent.overrideMessage) || 'Unknown error';

    track(trackableEvent.overrideMessage ?? `${trackableEvent.pageName} Error`, {
        action: trackableEvent.action,
        errorMessage: message,
        clientCode: trackableEvent.clientCode,
        ...trackableEvent.additionalMetadata,
    });
}

export {
    trackBasicEvent,
    trackEvent,
    handleTrackError,
    trackEventFromObject,
    trackEventFromErrorObject,
    TrackableEvent,
};